import { AfterViewInit, Component, Input, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { S1Table, S1ButtonType, S1DownloadService, S1UIService } from '@app/s1';
import { IAssistenza, StatoAssistenza, TipoAssistenza } from '@app/shared/models/assistenza';
import { AssistenzaService, IEsportaExcelAssistenzaParams, IEsportaMovimentiCassaParams, IImpoStatoAssistenzaMassivoParams, IRicercaAssistenzaParams, IRicercaTotaliTipoAssistenzaParams } from '@app/shared/services/assistenza.service';
import { UtilityService } from '@app/shared/services/utility.service';

export enum TipoAssistenzaReportType {
  uscite = "uscite",
  denaroRitrovato = "denaroRitrovato"
}

interface ITipoAssistenzaReportStyle {
  class: string;
  icon: string;
  label: string;
}

@Component({
  selector: 'ser-tipo-assistenza-report',
  templateUrl: './tipo-assistenza-report.component.html',
  styleUrl: './tipo-assistenza-report.component.scss'
})
export class TipoAssistenzaReportComponent implements AfterViewInit {

  @Input() type: TipoAssistenzaReportType = TipoAssistenzaReportType.uscite;
  @Input() set idStazione(newValue: string) {
    this._idStazione = newValue;
  }
  @Input() set dataDa(newValue: Date) {
    this._dataDa = newValue;
  }
  @Input() set dataA(newValue: Date) {
    this._dataA = newValue;
  }

  @ViewChild('table') table: S1Table<IAssistenza>;

  closeAllControl = new UntypedFormControl(false, null);

  s1ButtonType = S1ButtonType;

  styles: { [key in TipoAssistenzaReportType] : ITipoAssistenzaReportStyle } = {
    uscite: { class: 'bg-danger', icon:'fa-sign-out-alt', label: 'tipoAssistenzaReport.uscite'  },
    denaroRitrovato: { class: 'bg-info', icon:'fa-coins', label: 'tipoAssistenzaReport.denaroRitrovato' }
  }

  _idStazione: string;
  _dataDa: Date;
  _dataA: Date;

  totale: number = 0;

  constructor(private router: Router, private assistenzaService: AssistenzaService, private utility: UtilityService, private ui: S1UIService, private s1DownloadService: S1DownloadService) { }

  ngAfterViewInit(): void {
    this.searchAssistenze();
    this.searchTotali();
  }

  private mapParams() {

    const params: IRicercaAssistenzaParams = {
      page: this.table?.config.pagination.page.page,
      pageSize: +this.table?.config.pagination.page.itemsPerPage,
      sortBy: "data descending",
      tipi: this.type == TipoAssistenzaReportType.denaroRitrovato ? [TipoAssistenza.DenaroRitrovato] : [TipoAssistenza.RimborsoScontriniDiCredito, TipoAssistenza.AssistenzaCasse],
      idStazione: this._idStazione,
      dataDa: this.utility.UTCDateFromLocal(this._dataDa),
      dataA: this.utility.UTCDateFromLocal(this._dataA, true) 
    }

    return params;

  }

  searchAssistenze() {

    const params = this.mapParams();
    // mostro solo le assistenze che concorrono al fondo (da Confermato in su),
    // coerente con il totale in testata e con l'export PDF
    params.stati = [StatoAssistenza.Confermato, StatoAssistenza.ChiusoCasse, StatoAssistenza.ChiusoPagamenti];

    this.assistenzaService.ricerca(params).subscribe(assistenzePaginate => {
      this.table.updateData(assistenzePaginate.elements, assistenzePaginate.paginationInfo)
    })

  }

  searchTotali() {

    if (!this._idStazione || !this._dataDa || !this._dataA) {
      return
    }

    const params: IRicercaTotaliTipoAssistenzaParams = {
      idStazione: this._idStazione,
      dataDa: this.utility.UTCDateFromLocal(this._dataDa),
      dataA: this.utility.UTCDateFromLocal(this._dataA, true)
    }

    this.assistenzaService.ricercaTotali(params).subscribe(totali => {

      if (this.type == TipoAssistenzaReportType.uscite) {
        this.totale = totali.filter(tipo => tipo.tipo == TipoAssistenza.RimborsoScontriniDiCredito || tipo.tipo == TipoAssistenza.AssistenzaCasse).map(tipo => tipo.importo).reduce((sum, val) => sum + val, 0);
      } else if (this.type == TipoAssistenzaReportType.denaroRitrovato) {
        this.totale = totali.find(tipo => tipo.tipo == TipoAssistenza.DenaroRitrovato)?.importo ?? 0;
      }

    })

  }

  askCloseAll() {

    this.ui.showDialogPopup("assistenze.messages.closeAll").then(result => {

      if (result.value) {

        this.closeAll();

      }

    })

  }

  private closeAll() {

    const params: IImpoStatoAssistenzaMassivoParams = this.mapParams() as IImpoStatoAssistenzaMassivoParams;
    params.page = 1;
    params.pageSize = 9999;
    params.statoToSet = StatoAssistenza.ChiusoCasse;

    this.assistenzaService.impostaStatoMassivo(params).subscribe(success => {

      if (success) {
        this.ui.showSuccessToast("assistenze.messages.closedAll");
        this.searchAssistenze();
        this.esportaAssistenze();
      }

    })

  }

  esportaAssistenze() {

    const params: IEsportaExcelAssistenzaParams = this.mapParams();
    params.page = 1;
    params.pageSize = 9999;

    this.assistenzaService.esportaExcelAssistenza(params).subscribe(excel => {

      if (excel) {

        this.s1DownloadService.download(excel, "report.xlsx");

      }

    })

  }

  // Etichetta del comando PDF: "Esporta rimborsi" per le Uscite, "Esporta entrate" per il Denaro ritrovato
  get pdfLabel(): string {
    return this.type == TipoAssistenzaReportType.denaroRitrovato
      ? 'tipoAssistenzaReport.esportaEntrate'
      : 'tipoAssistenzaReport.esportaRimborsi';
  }

  // Esportazione PDF "D23_03 - Movimenti di cassa" (rimborsi per Uscite, entrate per Denaro ritrovato)
  esportaPdf() {

    const params: IEsportaMovimentiCassaParams = {
      idStazione: this._idStazione,
      dataDa: this.utility.UTCDateFromLocal(this._dataDa),
      dataA: this.utility.UTCDateFromLocal(this._dataA, true)
    }

    const isEntrate = this.type == TipoAssistenzaReportType.denaroRitrovato;
    const call = isEntrate
      ? this.assistenzaService.esportaEntratePdf(params)
      : this.assistenzaService.esportaRimborsiPdf(params);

    call.subscribe(pdf => {

      if (pdf) {
        this.s1DownloadService.download(pdf, isEntrate ? "Entrate.pdf" : "Rimborsi.pdf");
      }

    })

  }

  goToDetail(assistenza: IAssistenza) {
    // stessa scheda, con pagina di ritorno al dettaglio del fondo stazione
    this.router.navigate(['/assistenze', assistenza?.id], { queryParams: { from: this.router.url } });
  }

}
